import { debug } from './debug';

export const ALLOWED_PARENT_ORIGINS = [
    'http://localhost:3000', // Playground, run locally
    'http://localhost:5173', // the sample parent rig
    'https://playground.taxwallet.io',
];

export type AuthRecoveryReason = 'no-token' | 'refresh-failed' | 'session-expired';

const THROTTLE_MS = 24 * 60 * 60 * 1000;

/**
 * One signal per page load, whichever trigger gets there first.
 *
 * Not keyed by applicant: the mount check knows the id and the 419 interceptor
 * does not, so keying would let the same dead session ask twice. Each ask makes
 * the parent mint, and minting revokes the previously issued code — so a double
 * ask can hand the parent a code that its own second call already killed.
 */
let emittedThisLoad = false;

// postMessage takes a full URL but only compares its origin; an absent referrer (Referrer-Policy:
// no-referrer on the parent) means we cannot name a target, so we fall back to '*' and drop the id
const parentOrigin = (): string | null => {
    try {
        const origin = new URL(document.referrer).origin;
        if (ALLOWED_PARENT_ORIGINS.includes(origin)) return origin;
        debug('auth', `parent origin ${origin} is not allowlisted, falling back to '*'`, ALLOWED_PARENT_ORIGINS);
        return null;
    } catch {
        debug('auth', "no usable document.referrer, falling back to '*'", { referrer: document.referrer });
        return null;
    }
};

/**
 * Tell the parent frame this user's session is unrecoverable so it can mint a new opaque token.
 * Throttled to once per day per applicant, on attempt rather than on success — a recovery that
 * keeps failing must not loop. Returns whether a message was actually posted.
 */
export const requestAuthRecovery = (
    /** Throttle bucket. May be either id — it never leaves this browser. */
    applicantId: string | null,
    reason: AuthRecoveryReason,
    /**
     * The partner's own applicant id (the `aid` claim) — the only id they can
     * pass to GET /user. Deliberately not defaulted to `?id=`, which the
     * documented embed sets to Halcyon's internal uid and which that endpoint
     * looks up by applicantId, so it would 404.
     */
    partnerApplicantId?: string | null,
): boolean => {
    debug('auth', `recovery requested (reason=${reason}, applicantId=${partnerApplicantId ?? 'not resolvable'})`);

    if (window.parent === window) {
        debug('auth', 'not in an iframe, nothing to signal');
        return false;
    }

    if (emittedThisLoad) {
        debug('auth', 'already signalled on this page load, suppressed');
        return false;
    }

    const key = `authRecovery:${applicantId ?? 'unknown'}`;

    try {
        const last = Number(localStorage.getItem(key));
        if (last && Date.now() - last < THROTTLE_MS) {
            const hours = ((THROTTLE_MS - (Date.now() - last)) / 3600000).toFixed(1);
            debug('auth', `throttled for ${key}, ${hours}h remaining`, { lastAttempt: new Date(last).toISOString() });
            return false;
        }
        localStorage.setItem(key, String(Date.now()));
    } catch {
        // storage unavailable (partitioned or blocked): signal anyway, the in-memory guard still holds
        debug('auth', 'localStorage unavailable, relying on the in-memory guard only');
    }
    emittedThisLoad = true;

    const target = parentOrigin();
    const payload = {
        type: 'auth-expired',
        source: 'halcyondirect',
        reason,
        // withheld when we cannot name the target, or when we cannot tell which id
        // we hold; the parent always knows its own applicant id regardless
        ...(target && partnerApplicantId ? { applicantId: partnerApplicantId } : {}),
    };

    try {
        window.parent.postMessage(payload, target ?? '*');
        debug('auth', `posted auth-expired to ${target ?? '*'}`, payload);
        return true;
    } catch (error) {
        console.error('Error signalling auth-expired to parent:', error);
        debug('auth', 'postMessage threw; the attempt is still recorded so it will not loop', error);
        return false;
    }
};
